import { Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { WalletCurrencyTagIcon, WalletDefaultIcon, WalletVisibilityIcon } from 'BreetAssets';
import { type CURRENCYCODETYPE, CURRENCYCONFIG, type CURRENCYSYMBOLTYPE } from 'BreetHelpers';
import { useWalletBalance, useWithdrawRequestApi } from 'BreetHooks';
import { selectIsFiatBalanceHidden, setIsFiatBalanceHidden, useAppDispatch, useAppSelector } from 'BreetRedux';
import { useCallback } from 'react';

import type { IWalletBalance } from './WalletBalance.helpers';

const WalletBalance = ({ withdraw, fiat, isSettlementWallet }: IWalletBalance) => {
	const { withdrawFunds, withdrawFundsUsd } = useWithdrawRequestApi();

	const dispatch = useAppDispatch();

	const hideAmount = useAppSelector(selectIsFiatBalanceHidden);
	const hiddenAmount = '*******';

	const { getBalance, wallets } = useWalletBalance();

	const balance = getBalance(fiat);

	const walletBalance = wallets && !hideAmount ? balance : hiddenAmount;

	const handleVisibility = useCallback(() => {
		if (withdraw) return;

		dispatch(setIsFiatBalanceHidden(!hideAmount));
	}, [dispatch, hideAmount, withdraw]);

	const uiConfigObj: Record<
		CURRENCYCODETYPE,
		{ currency: CURRENCYCODETYPE; symbol: CURRENCYSYMBOLTYPE; withdrawHandler?: () => void }
	> = {
		USD: {
			currency: CURRENCYCONFIG.USD.code,
			symbol: CURRENCYCONFIG.USD.symbol,
			withdrawHandler: withdrawFundsUsd,
		},
		NGN: {
			currency: CURRENCYCONFIG.NGN.code,
			symbol: CURRENCYCONFIG.NGN.symbol,
			withdrawHandler: withdrawFunds,
		},
		GHS: {
			currency: CURRENCYCONFIG.GHS.code,
			symbol: CURRENCYCONFIG.GHS.symbol,
			withdrawHandler: withdrawFunds,
		},
	};

	const uiConfig = uiConfigObj[fiat];

	return (
		<div
			className={classNameMerger('card walletbalance', 'bg-primary-400 border-dark-navy')}
			data-testid='wallet-balance'
		>
			<div className='walletbalance_content'>
				<div className='walletbalance_content_header'>
					<WalletCurrencyTagIcon variant={uiConfig.currency.toUpperCase() as CURRENCYCODETYPE} />

					{isSettlementWallet && <WalletDefaultIcon />}
				</div>

				<div className='walletbalance_content_body'>
					<Typography
						as='h6'
						className='color-neutral-50'
					>
						<span>{hideAmount ? '' : uiConfig.symbol}</span>
						{walletBalance}
					</Typography>

					<WalletVisibilityIcon
						show={!hideAmount}
						className='walletbalance_content_visibility cursor_pointer'
						onClick={handleVisibility}
					/>
				</div>
			</div>
		</div>
	);
};

export default WalletBalance;
